import React from 'react';
import { Link } from 'react-router-dom';
import { useTenant } from '../../../context/TenantContext';

const DEFAULT_HEADING = 'Three ways to take this.';
const DEFAULT_SUBHEAD =
  'Per-programme pricing for individuals, an annual pass for serial learners, and team packages for engineering orgs.';

// Hidden when tenant has not supplied plans — pricing is too tenant-specific to fabricate.
export const UtilityPricing: React.FC = () => {
  const { tenant } = useTenant();
  const pricing = tenant?.branding.marketing?.pricing;
  if (!pricing || !pricing.plans || pricing.plans.length === 0) return null;

  const heading = pricing.heading?.trim() || DEFAULT_HEADING;
  const subhead = pricing.subhead?.trim() || DEFAULT_SUBHEAD;
  const cols = pricing.plans.length;
  const colTemplate = cols === 1 ? '1fr' : cols === 2 ? '1fr 1fr' : 'repeat(3, 1fr)';

  return (
    <section id="pricing" className="util-pricing py-20 bg-white border-b border-[var(--util-rule)]">
      <div className="max-w-[1320px] mx-auto px-6">
        <div className="grid md:grid-cols-[220px_1fr] gap-10 mb-12 items-start">
          <div className="font-util-mono text-[11px] uppercase tracking-[0.06em] text-[var(--util-ink-faint)] border-t-2 border-[var(--util-ink)] pt-3">
            <span className="block text-[var(--util-accent-ink)] mb-1.5 font-medium">§ 03</span>
            Access
          </div>
          <div>
            <h2 className="font-util-sans text-3xl md:text-[44px] font-semibold tracking-[-0.025em] leading-[1.05] max-w-[24ch]">
              {heading}
            </h2>
            <p className="text-[var(--util-ink-soft)] text-base max-w-[60ch] mt-3">{subhead}</p>
          </div>
        </div>

        <div
          className="grid border border-[var(--util-rule)] bg-white rounded-md overflow-hidden"
          style={{ gridTemplateColumns: colTemplate }}
        >
          {pricing.plans.map((plan, i) => (
            <div
              key={i}
              className={`util-plan p-7 flex flex-col ${
                i < pricing.plans.length - 1 ? 'md:border-r border-[var(--util-rule)]' : ''
              } ${plan.featured ? 'util-plan-featured bg-[var(--util-ink)] text-white relative' : ''}`}
            >
              {plan.featured && (
                <span className="absolute top-3 right-3 bg-[var(--util-accent)] text-white font-util-mono text-[9px] tracking-[0.06em] px-2 py-0.5 rounded">
                  RECOMMENDED
                </span>
              )}
              <div
                className={`font-util-mono text-[11px] uppercase tracking-[0.08em] mb-2 ${
                  plan.featured ? 'text-[var(--util-accent-soft)]' : 'text-[var(--util-ink-faint)]'
                }`}
              >
                {plan.name}
              </div>
              <h3 className="font-util-sans font-semibold text-[22px] tracking-[-0.015em] mb-4">{plan.tagline}</h3>
              <div className="font-util-sans font-semibold text-[36px] tracking-[-0.025em] leading-none mb-1">
                {plan.price}
              </div>
              {plan.pricePer && (
                <div
                  className={`font-util-mono text-[11px] mb-6 ${
                    plan.featured ? 'text-[var(--util-accent-soft)]' : 'text-[var(--util-ink-faint)]'
                  }`}
                >
                  {plan.pricePer}
                </div>
              )}
              <ul className="flex-1 list-none">
                {plan.features.map((f, j) => (
                  <li
                    key={j}
                    className={`text-[14px] py-2 flex items-start gap-2.5 border-b ${
                      plan.featured ? 'border-white/8' : 'border-[#e5e3dd]'
                    } last:border-b-0`}
                  >
                    <span className="font-util-mono text-[var(--util-accent)] font-semibold flex-shrink-0">+</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                to={plan.ctaHref}
                className={`mt-6 text-center py-3 rounded-md font-semibold text-sm transition-colors ${
                  plan.featured
                    ? 'bg-[var(--util-accent)] text-white hover:bg-[var(--util-accent-ink)]'
                    : 'bg-[var(--util-mute)] text-[var(--util-ink)] hover:bg-[var(--util-accent)] hover:text-white'
                }`}
              >
                {plan.ctaLabel}
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
